import { Box, Card, ControlledTabs, Flex, Icon, TabItem, theme, Typography } from '@fe-toolkit/web-components/ssr'

import { Dispatch, SetStateAction, useCallback, useMemo, useRef } from 'react'
import Router from 'next/router'

import { z } from 'zod'

import { EVisitType, TimeSlotSchema, transformedAppointmentResponseSchema } from '@giffgaff/broadband-common'

import { TCustomAxiosErrorBody } from '@fe-toolkit/react-query-hooks-toolkit'

import { AxiosError } from 'axios'

import {
  AppointmentInfoUpdate,
  appointmentUpdate,
  appointmentUpdatesObject,
} from '@/components/pages/ChangeInstallDates/ChangeInstallDates.container'

import OrderOverview from './components/OrderOverview/OrderOverview'
import ChangeAppointmentInfoContainer from './components/ChangeAppointmentInfo/ChangeAppointmentInfo.container'

import { appointmentCanBeAmended, appointmentIsComplete, compareDateAndSlot } from './utils/utils'
import {
  HandleDateChange,
  HandleTimeSlotChange,
  EOrderStage,
  TManagedVisitType,
  UpdatedAppointmentsObj,
  GroupedPlanAppointmentNotes,
} from './utils/propTypeHelpers'

import type { OrderDetailActiveAppointments } from '@/api'

export type ChangeInstallProps = {
  orderStage?: EOrderStage
  activeAppointments?: OrderDetailActiveAppointments[]
  orderId?: string
  isOrderLoading?: boolean
  isUpdatingAppointments?: boolean
  isUpdatingAppointmentsError?: boolean
  handleDateChange: HandleDateChange
  handleTimeSlotChange: HandleTimeSlotChange
  handleSaveNewAppointments: (appointments: appointmentUpdatesObject) => void
  updatedAppointments?: appointmentUpdate[]
  leadTimeClash?: boolean
  appointmentInfoUpdate: {
    homeInstall: AppointmentInfoUpdate
    outsideInstall: AppointmentInfoUpdate
  }
  onInfoChange: {
    [EVisitType.IN_HOME]: Dispatch<SetStateAction<AppointmentInfoUpdate>>
    [EVisitType.PRE_WORK]: Dispatch<SetStateAction<AppointmentInfoUpdate>>
  }
  requestedAppointmentNoLongerAvailable: boolean
  rebookingRequiredBlocked?: boolean
  appointments?: z.infer<typeof transformedAppointmentResponseSchema> // This is being passed down for the calendar to show available dates, but ideally the calendar should be able to work with the groupedActiveAppointments data instead to avoid prop drilling
  getAppointmentsError?: AxiosError<TCustomAxiosErrorBody> | null
  groupedPlanAppointmentNotes?: GroupedPlanAppointmentNotes
  isFetching: boolean
  isLoading: boolean
  currentTabNumber: number
  setCurrentTabNumber: Dispatch<SetStateAction<number>>
  singleAppointment?: boolean
}

const ChangeInstallDates = ({
  orderId,
  orderStage,
  isUpdatingAppointments = false,
  isUpdatingAppointmentsError = false,
  handleDateChange,
  handleTimeSlotChange,
  handleSaveNewAppointments,
  activeAppointments = [],
  updatedAppointments,
  leadTimeClash = false,
  appointmentInfoUpdate,
  onInfoChange,
  requestedAppointmentNoLongerAvailable,
  rebookingRequiredBlocked = false,
  appointments,
  getAppointmentsError,
  groupedPlanAppointmentNotes = {},
  isFetching,
  isLoading,
  currentTabNumber,
  setCurrentTabNumber,
  singleAppointment = false,
}: ChangeInstallProps) => {
  const groupedActiveAppointments = useMemo<{
    [EVisitType.PRE_WORK]?: OrderDetailActiveAppointments
    [EVisitType.IN_HOME]?: OrderDetailActiveAppointments
  }>(() => {
    return {
      [EVisitType.PRE_WORK]: activeAppointments.find(appointment => appointment.visitType === EVisitType.PRE_WORK),
      [EVisitType.IN_HOME]: activeAppointments.find(appointment => appointment.visitType === EVisitType.IN_HOME),
    }
  }, [activeAppointments])

  const groupedUpdatedAppointments: UpdatedAppointmentsObj = useMemo(() => {
    return {
      [EVisitType.PRE_WORK]: updatedAppointments?.find(appointment => appointment.visitType === EVisitType.PRE_WORK),
      [EVisitType.IN_HOME]: updatedAppointments?.find(appointment => appointment.visitType === EVisitType.IN_HOME),
    }
  }, [updatedAppointments])

  const isApptUpdated = {
    [EVisitType.PRE_WORK]: compareDateAndSlot(
      groupedActiveAppointments[EVisitType.PRE_WORK],
      groupedUpdatedAppointments[EVisitType.PRE_WORK],
    ),
    [EVisitType.IN_HOME]: compareDateAndSlot(
      groupedActiveAppointments[EVisitType.IN_HOME],
      groupedUpdatedAppointments[EVisitType.IN_HOME],
    ),
  }

  const handleCancelAppointmentChange = () => {
    Router.push(`/manage-order/${orderId}`)
  }

  const preWorkIsAmendable: boolean = appointmentCanBeAmended(activeAppointments).preWork
  const inHomeIsAmendable: boolean = appointmentCanBeAmended(activeAppointments).inHome

  const preWorkIsComplete: boolean = appointmentIsComplete(orderStage as EOrderStage, EVisitType.PRE_WORK)
  const inHomeIsComplete: boolean = appointmentIsComplete(orderStage as EOrderStage, EVisitType.IN_HOME)

  const handleInfoUpdate = (
    field: 'contactFirstName' | 'contactLastName' | 'contactPhoneNumber',
    value: string,
    destination: TManagedVisitType,
  ) => {
    onInfoChange[destination](prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSelectedNotesChange = (destination: TManagedVisitType) => (_field: string, value: string[]) => {
    onInfoChange[destination](prev => ({
      ...prev,
      selectedNotes: value,
    }))
  }

  const handleRequestedCallChange = (destination: TManagedVisitType) => (value: boolean) => {
    onInfoChange[destination](prev => ({
      ...prev,
      requestedCall: value,
    }))
  }

  const scrollRef = useRef<HTMLDivElement>(null)

  const scrollToRef = () => {
    if (scrollRef.current) {
      const top = scrollRef.current?.getBoundingClientRect().top + window.scrollY - 64

      window.scrollTo({ top, behavior: 'smooth' })
    }
  }

  const isFinalTab = (currentTabNumber: number) => singleAppointment || currentTabNumber === 1

  const singleAppointmentVisitType = useMemo<TManagedVisitType | null>(() => {
    if (!singleAppointment) return null

    if (orderStage === EOrderStage.IN_HOME && groupedActiveAppointments[EVisitType.IN_HOME]) {
      return EVisitType.IN_HOME
    }

    if (groupedActiveAppointments[EVisitType.PRE_WORK] && !groupedActiveAppointments[EVisitType.IN_HOME]) {
      return EVisitType.PRE_WORK
    }

    if (groupedActiveAppointments[EVisitType.IN_HOME]) {
      return EVisitType.IN_HOME
    }

    return null
  }, [groupedActiveAppointments, orderStage, singleAppointment])

  const handleMainButtonClick = async (date: string | undefined, timeSlot: TimeSlotSchema | undefined) => {
    // this is where we either move to the next tab or submit the api call
    if (isFinalTab(currentTabNumber)) {
      // this is the in-home install.
      // the empty strings for contact details will be populated in the handleSaveNewAppointments function.
      handleSaveNewAppointments({
        prework: {
          date: updatedAppointments?.find(appointment => appointment.visitType === EVisitType.PRE_WORK)?.date || '',
          slot:
            updatedAppointments?.find(appointment => appointment.visitType === EVisitType.PRE_WORK)?.slot.slot || 'AM',
          visitType: EVisitType.PRE_WORK,
          contactFirstName: '',
          contactLastName: '',
          contactPhoneNumber: '',
        },
        inHome: {
          date: date || groupedActiveAppointments?.[EVisitType.IN_HOME]?.appointmentDate || '',
          slot: timeSlot?.slot || groupedActiveAppointments?.[EVisitType.IN_HOME]?.slot || '', // this should fall back to the initial slot if undefined
          visitType: EVisitType.IN_HOME,
          contactFirstName: '',
          contactLastName: '',
          contactPhoneNumber: '',
        },
      })
    } else {
      // these are the pre-work install actions
      setCurrentTabNumber(1) // switch to the in-home install tab
      scrollToRef()
    }
  }

  const renderWithOptionalTabs = useCallback(
    (components: React.ReactNode[]) => {
      if (!singleAppointment) {
        return (
          <ControlledTabs
            // $addTopRadius={true} // this got lost in the migration to fe-toolkit.
            activeTab={currentTabNumber}
            ariaLabel="Manage Install Dates Tabs"
            setActiveTab={setCurrentTabNumber}
          >
            {/* Outside install tab */}
            <TabItem tabClick={() => setCurrentTabNumber(0)} tabTitle={'Outside install'}>
              {components[0]}
            </TabItem>
            {/* In home install tab */}
            <TabItem
              tabClick={() => setCurrentTabNumber(1)}
              tabTitle={
                <Flex sx={{ justifyContent: 'center' }}>
                  {leadTimeClash && (
                    <Box sx={{ marginRight: theme.spacing.tiny }}>
                      <Icon
                        color={theme.colors.red}
                        height={'24'}
                        iconKey="ExclamationMarkFilled"
                        sx={{ marginRight: theme.spacing.tiny }}
                        width={'24'}
                      />
                    </Box>
                  )}
                  <Box>Home install</Box>
                </Flex>
              }
            >
              {components[1]}
            </TabItem>
          </ControlledTabs>
        )
      } else if (singleAppointmentVisitType === EVisitType.PRE_WORK) {
        return (
          <Card sx={{ backgroundColor: 'white', padding: theme.spacing.small }}>
            <Typography as="h2" sx={({ theme }) => ({ margin: `0 0 ${theme.spacing.medium}` })} variant="shout">
              Manage outside install
            </Typography>
            <span id="tab-panel-0">{components[0]}</span>
          </Card>
        )
      } else if (singleAppointmentVisitType === EVisitType.IN_HOME) {
        return (
          <Card sx={{ backgroundColor: 'white', padding: theme.spacing.small }}>
            <Typography as="h2" sx={({ theme }) => ({ margin: `0 0 ${theme.spacing.medium}` })} variant="shout">
              Manage home install
            </Typography>
            <span id="tab-panel-1">{components[1]}</span>
          </Card>
        )
      } else {
        return null
      }
    },
    [currentTabNumber, leadTimeClash, singleAppointmentVisitType, setCurrentTabNumber, singleAppointment],
  )

  return (
    <Flex
      sx={({ theme }) => ({
        flexDirection: 'column',
        padding: '0',
        [theme.breakpoints.up('medium')]: {
          flexDirection: 'row-reverse',
          justifyContent: 'flex-end',
          alignItems: 'start',
        },
      })}
    >
      {/* current order overview */}
      <OrderOverview
        groupedActiveAppointments={groupedActiveAppointments}
        groupedUpdatedAppointments={groupedUpdatedAppointments}
        inHomeIsAmendable={inHomeIsAmendable}
        inHomeIsComplete={inHomeIsComplete}
        isApptUpdated={isApptUpdated}
        preWorkIsAmendable={preWorkIsAmendable}
        preWorkIsComplete={preWorkIsComplete}
      />

      {/* appointment tabs */}
      <Card
        ref={scrollRef}
        backgroundColor={'transparent'}
        sx={({ theme }) => ({
          padding: '0',
          [theme.breakpoints.up('medium')]: {
            margin: theme.spacing.small,
            width: '50%',
            maxWidth: '430px',
          },
        })}
      >
        {renderWithOptionalTabs([
          <ChangeAppointmentInfoContainer
            key={EVisitType.PRE_WORK}
            appointmentInfoUpdate={appointmentInfoUpdate.outsideInstall}
            appointments={appointments}
            getAppointmentsError={getAppointmentsError}
            groupedActiveAppointments={groupedActiveAppointments}
            handleAppointmentInfoUpdate={handleInfoUpdate}
            handleCancelAppointmentChange={handleCancelAppointmentChange}
            handleDateChange={handleDateChange}
            handleMainButtonClick={handleMainButtonClick}
            handleRequestedCallChange={handleRequestedCallChange(EVisitType.PRE_WORK)}
            handleTimeSlotChange={handleTimeSlotChange}
            isFetching={isFetching}
            isFinalTab={isFinalTab(0)}
            isLoading={isLoading}
            isUpdatingAppointments={isUpdatingAppointments}
            isUpdatingAppointmentsError={isUpdatingAppointmentsError}
            leadTimeClash={leadTimeClash}
            planAppointmentNotes={groupedPlanAppointmentNotes[EVisitType.PRE_WORK]}
            rebookingRequiredBlocked={rebookingRequiredBlocked}
            relevantActiveAppointment={groupedActiveAppointments[EVisitType.PRE_WORK]!}
            requestedAppointmentNoLongerAvailable={requestedAppointmentNoLongerAvailable}
            setSelectedNotes={handleSelectedNotesChange(EVisitType.PRE_WORK)}
            singleAppointment={singleAppointment}
            updatedAppointments={updatedAppointments || []}
          />,
          <ChangeAppointmentInfoContainer
            key={EVisitType.IN_HOME}
            appointmentInfoUpdate={appointmentInfoUpdate.homeInstall}
            appointments={appointments}
            getAppointmentsError={getAppointmentsError}
            groupedActiveAppointments={groupedActiveAppointments}
            handleAppointmentInfoUpdate={handleInfoUpdate}
            handleCancelAppointmentChange={handleCancelAppointmentChange}
            handleDateChange={handleDateChange}
            handleMainButtonClick={handleMainButtonClick}
            handleRequestedCallChange={handleRequestedCallChange(EVisitType.IN_HOME)}
            handleTimeSlotChange={handleTimeSlotChange}
            isFetching={isFetching}
            isFinalTab={isFinalTab(1)}
            isLoading={isLoading}
            isUpdatingAppointments={isUpdatingAppointments}
            isUpdatingAppointmentsError={isUpdatingAppointmentsError}
            leadTimeClash={leadTimeClash}
            planAppointmentNotes={groupedPlanAppointmentNotes[EVisitType.IN_HOME]}
            rebookingRequiredBlocked={rebookingRequiredBlocked}
            relevantActiveAppointment={groupedActiveAppointments[EVisitType.IN_HOME]!}
            requestedAppointmentNoLongerAvailable={requestedAppointmentNoLongerAvailable}
            setSelectedNotes={handleSelectedNotesChange(EVisitType.IN_HOME)}
            singleAppointment={singleAppointment}
            updatedAppointments={updatedAppointments || []}
          />,
        ])}
      </Card>
    </Flex>
  )
}

export default ChangeInstallDates
